import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Calendar, Users, Heart, XCircle, Sparkles, ArrowRight } from "lucide-react";

const ProducteevHistory = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Producteev History: The Social Task Manager That Vanished Without Warning",
    "description": "The complete history of Producteev, from its 2008 launch to Jive's acquisition and silent shutdown in May 2018.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/producteev/history/"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Producteev",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "iOS, Android, Web"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Producteev History: The Social Task Manager That Vanished Without Warning | CSW</title>
        <meta name="description" content="The complete history of Producteev, from its 2008 launch to Jive's acquisition and silent shutdown in May 2018." />
        <meta name="keywords" content="Producteev history, Producteev shutdown, Jive Software, task management history, Ilan Abehassera" />
        <link rel="canonical" href="https://chillstudy.work/producteev/history/" />
        <meta property="og:title" content="Producteev History: The Social Task Manager That Vanished Without Warning" />
        <meta property="og:description" content="The complete history of Producteev, from its 2008 launch to its silent shutdown in 2018." />
        <meta property="og:url" content="https://chillstudy.work/producteev/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/producteev/" className="hover:text-primary transition-colors">Producteev Alternative</Link></li>
              <li>/</li>
              <li className="text-foreground">History</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Producteev: The Social Task Manager That Vanished Without Warning
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Producteev pioneered the concept of "social task management," treating tasks not just as to-dos but as 
              collaborative objects. After a decade of service, it disappeared with almost no notice.
            </p>
          </header>

          {/* Timeline Section */}
          <section className="mb-16">
            <div className="space-y-8">
              {/* Origins */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Origins and Innovation (2008-2012)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Producteev was founded in 2008 by <a href="https://tracxn.com/d/companies/producteev" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ilan Abehassera</a> (with 
                  co-founder Aric Lasry) in New York City. It launched as one of the first "social task management" platforms, 
                  treating tasks as collaborative objects that teams could work on together.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Key features included the ability to assign tasks to multiple people, create shared projects, attach files 
                  from services like Dropbox, tag tasks for categorization, and get email reminders. It offered an activity 
                  feed showing who completed what—pioneering features that are now standard in project management tools.
                </p>
              </article>

              {/* Why People Loved It */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Why People Loved It</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Cross-Platform Excellence</h3>
                    <p>Producteev had a robust web app, native mobile apps for iOS and Android, a desktop app, and even an Outlook plugin. All tasks synced in real-time.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Completely Free</h3>
                    <p>In May 2013, Producteev made a bold move: it dropped all pricing tiers and became completely free for unlimited team use—a rarity in the productivity space.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Task as Teamwork</h3>
                    <p>Producteev was a pioneer in showing how tasks could be the heart of teamwork, with comments, assignments, and file attachments all in one place.</p>
                  </div>
                </div>
              </article>

              {/* Jive Acquisition */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                  <h2 className="text-2xl font-bold">Jive Software Acquisition (2012)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In November 2012, <a href="https://techcrunch.com/2012/11/05/jive-software-acquires-meetings-io-and-producteev/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Jive Software acquired Producteev</a> and 
                  integrated its task features into Jive's social collaboration suite while keeping Producteev running as a standalone product.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  In 2013, Jive released a redesigned "Producteev by Jive" with an updated interface and deeper features, 
                  touting it as "the leading social task management solution" for teams of all sizes. Under Jive, Producteev 
                  continued to operate for several years.
                </p>
              </article>

              {/* The Shutdown */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <XCircle className="w-6 h-6 text-destructive" />
                  </div>
                  <h2 className="text-2xl font-bold">The Silent Shutdown (2018)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Things changed after 2017 when Jive Software was itself acquired by private equity firm Aurea. In early 2018, 
                  without much warning, <strong className="text-foreground">Producteev was shut down on May 11, 2018</strong>.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  What shocked users was the lack of communication—<strong className="text-foreground">no official public announcement</strong> was 
                  widely broadcast. Users found out via a terse note that the service would be discontinued. Many were caught off guard 
                  by this abrupt end after nearly a decade of use.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Some users reported that Jive disabled logins abruptly around the shutdown date, forcing teams to scramble 
                  to export their task data and find a new solution.
                </p>
              </article>

              {/* Community Reaction */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Community Reaction and Legacy</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The reaction from Producteev's community was one of frustration and urgency. On forums and Reddit, users 
                  expressed anger that an app they relied on was killed "without notice and without allowing users to backup 
                  their data" ahead of time.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Some competitors stepped in to help—<a href="https://www.paymoapp.com/blog/free-producteev-alternative/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Paymo</a> informed 
                  readers of the shutdown and pitched their service as an alternative, noting "there's no official 
                  announcement from Jive...which is pretty weird."
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Ilan Abehassera, Producteev's founder, went on to other ventures (he later co-founded the CRM app "Heymarket"). 
                  Producteev's legacy lives on in the many task tools that now emphasize team collaboration on tasks—features 
                  that were relatively novel when Producteev introduced them.
                </p>
              </article>
            </div>
          </section>

          {/* Sources */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Sources</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://www.prnewswire.com/news-releases/producteev-by-jive-brings-social-task-management-to-the-businesses--teams-of-all-types--sizes---for-free-208283891.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">PRNewswire</a> on 
                  Producteev by Jive going completely free in 2013.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://techcrunch.com/2012/11/05/jive-software-acquires-meetings-io-and-producteev/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TechCrunch</a> on 
                  Jive Software's acquisition of Producteev in 2012.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://www.paymoapp.com/blog/free-producteev-alternative/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Paymo Blog</a> on 
                  the Producteev shutdown and alternatives.
                </span>
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Looking for a Producteev Alternative?</h2>
            <p className="text-muted-foreground mb-6">
              Discover how CSW combines task management with collaborative study sessions to help teams stay productive.
            </p>
            <Link
              to="/producteev/"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Explore CSW as Producteev Alternative
              <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProducteevHistory;
